import { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, Stack, Grid, TextField, MenuItem } from '@mui/material';
import { Plus } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useSchool } from '@/hooks/useSchool';
import teacherService from '@/services/teacherService';
import { useTeacherAssignments } from '@/hooks/useTeacherAssignments';
import { Loader } from '@/components/feedback/Loader';
import { SubjectBadge } from '@/features/teachers/components/SubjectBadge';
import toast from 'react-hot-toast';

export function ClassworkDiaryPage() {
  const { schoolId } = useSchool();
  const { user } = useAuth();
  const { assignments } = useTeacherAssignments(user?.teacherId);
  
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [classwork, setClasswork] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  // New entry form state
  const [form, setForm] = useState({ classId: '', sectionId: '', topicCovered: '', remarks: '' });

  useEffect(() => {
    if (schoolId && user?.teacherId && date) {
      setLoading(true);
      teacherService.getTeacherClasswork(schoolId, user.teacherId, date)
        .then(res => setClasswork(res.data))
        .finally(() => setLoading(false));
    }
  }, [schoolId, user?.teacherId, date]);

  const handleAdd = async () => {
    try {
      const assignment = assignments.find(a => a.classId === form.classId && a.sectionId === form.sectionId);
      if (!assignment) throw new Error("Invalid class/section selected");
      
      const res = await teacherService.saveClasswork(schoolId, user.teacherId, {
        ...form,
        date,
        className: assignment.className,
        sectionName: assignment.sectionName,
        subject: assignment.subject,
      });
      
      setClasswork([res.data, ...classwork]);
      toast.success('Classwork diary entry added');
      setIsAdding(false);
      setForm({ classId: '', sectionId: '', topicCovered: '', remarks: '' });
    } catch (err) {
      toast.error(err.message || 'Failed to add entry');
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Classwork Diary
          </Typography>
          <Typography color="text.secondary">
            Log your daily teaching activities and remarks
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Plus size={20} />} onClick={() => setIsAdding(true)} disabled={isAdding}>
          Add Entry
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <TextField
          label="Select Date"
          type="date"
          slotProps={{ inputLabel: { shrink: true } }}
          value={date}
          onChange={(e) => setDate(e.target.value)}
          sx={{ width: 250 }}
        />
      </Paper>

      {isAdding && (
        <Paper sx={{ p: 3, mb: 4, borderRadius: 2, border: 1, borderColor: 'primary.main' }}>
          <Typography variant="h6" mb={2}>New Entry for {date}</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField 
                select 
                fullWidth 
                label="Class" 
                value={form.classId} 
                onChange={(e) => setForm({...form, classId: e.target.value, sectionId: ''})}
              >
                {[...new Set(assignments?.map(a => Object.entries({id: a.classId, name: a.className})))].map(c => {
                  const [idPair, namePair] = c;
                  return <MenuItem key={idPair[1]} value={idPair[1]}>{namePair[1]}</MenuItem>
                })}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField 
                select 
                fullWidth 
                label="Section" 
                value={form.sectionId} 
                onChange={(e) => setForm({...form, sectionId: e.target.value})}
                disabled={!form.classId}
              >
                {assignments?.filter(a => a.classId === form.classId).map(s => (
                  <MenuItem key={s.sectionId} value={s.sectionId}>{s.sectionName} ({s.subject})</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField 
                fullWidth 
                label="Topic Covered" 
                value={form.topicCovered} 
                onChange={(e) => setForm({...form, topicCovered: e.target.value})}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField 
                fullWidth 
                label="Teacher Remarks / Reflections" 
                multiline 
                rows={3}
                value={form.remarks} 
                onChange={(e) => setForm({...form, remarks: e.target.value})}
              />
            </Grid>
            <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button onClick={() => setIsAdding(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleAdd} disabled={!form.sectionId || !form.topicCovered}>
                Save Entry
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {loading ? (
        <Loader />
      ) : classwork.length === 0 && !isAdding ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <Typography color="text.secondary">No classwork entries for this date.</Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {classwork.map(cw => (
            <Paper key={cw.id} sx={{ p: 3, borderRadius: 2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {cw.className} - {cw.sectionName}
                  </Typography>
                  <SubjectBadge subject={cw.subject} />
                </Box>
                <Typography variant="body2" color="text.secondary" align="right">
                  {cw.date}
                </Typography>
              </Stack>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">Topic Covered:</Typography>
                <Typography variant="body1">{cw.topicCovered}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Remarks:</Typography>
                <Typography variant="body2">{cw.remarks || '-'}</Typography>
              </Box>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
}
